import { ATTR } from './attrs';
import { readDisabled } from './why';
import { Box, FeatureRef, HelpCatalogue, Resolution, SectionRef } from './types';

/**
 * Pure DOM -> help target resolution. Reads only data-* attributes, geometry and the static
 * catalogue. It never fetches or reads business data.
 */

const sel = (attr: string) => `[${attr}]`;
const inUi = (el: Element | null) => !!el?.closest(sel(ATTR.ui));

const rectOf = (el: Element): Box => {
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
};
const isVisible = (b: Box) => b.right - b.left > 0 && b.bottom - b.top > 0;
const areaOf = (b: Box) => Math.max(0, b.right - b.left) * Math.max(0, b.bottom - b.top);
const hasPoint = (b: Box, x: number, y: number) => x >= b.left && x <= b.right && y >= b.top && y <= b.bottom;
const intersection = (a: Box, b: Box): Box => ({
    left: Math.max(a.left, b.left), top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right), bottom: Math.min(a.bottom, b.bottom),
});
const intersects = (a: Box, b: Box) => {
    const i = intersection(a, b);
    return i.right >= i.left && i.bottom >= i.top;
};
const centre = (b: Box) => ({ x: (b.left + b.right) / 2, y: (b.top + b.bottom) / 2 });

/** A real (non-help) modal is open: MUI marks everything behind it aria-hidden. */
const modalOpen = () => !!document.querySelector('.MuiModal-root:not(.MuiModal-hidden)');
const behindModal = (el: Element) => !!el.closest('[aria-hidden="true"], [inert]');

function tagged(attr: string, root: ParentNode = document): HTMLElement[] {
    const modal = modalOpen();
    return Array.from(root.querySelectorAll<HTMLElement>(sel(attr)))
        .filter(el => !inUi(el) && isVisible(rectOf(el)) && !(modal && behindModal(el)));
}

/** Smallest visible tagged element whose rectangle holds the point (works for pointer-events:none disabled buttons). */
function smallestAt(attr: string, x: number, y: number): HTMLElement | null {
    let best: HTMLElement | null = null;
    let bestArea = Infinity;
    for (const el of tagged(attr)) {
        const r = rectOf(el);
        if (hasPoint(r, x, y) && areaOf(r) < bestArea) { best = el; bestArea = areaOf(r); }
    }
    return best;
}

function taggedAtPoint(attr: string, x: number, y: number): HTMLElement | null {
    const hits = typeof document.elementsFromPoint === 'function' ? document.elementsFromPoint(x, y) : [];
    for (const h of hits) {
        if (inUi(h)) continue;
        const t = h.closest<HTMLElement>(sel(attr));
        if (t && !(modalOpen() && behindModal(t))) return t;
        break; // only the topmost real element is checked; the rect scan below covers pointer-events:none targets
    }
    return smallestAt(attr, x, y);
}

function sectionRef(el: HTMLElement | null, cat: HelpCatalogue, missing: string[], attr: string = ATTR.section): SectionRef | null {
    if (!el) return null;
    const key = el.getAttribute(attr) ?? '';
    const entry = cat.sections[key];
    if (!entry) { if (key) missing.push(key); return null; }
    return { key, el, entry };
}

function featureRefs(els: HTMLElement[], cat: HelpCatalogue, missing: string[]): FeatureRef[] {
    const seen = new Set<string>();
    const out: FeatureRef[] = [];
    const ordered = [...els].sort((a, b) => {
        const ra = rectOf(a), rb = rectOf(b);
        return ra.top - rb.top || ra.left - rb.left;
    });
    for (const el of ordered) {
        const key = el.getAttribute(ATTR.element) ?? '';
        if (!key || seen.has(key)) continue;
        seen.add(key);
        const entry = cat.elements[key];
        if (!entry) { missing.push(key); continue; }
        out.push({ key, el, entry, disabled: readDisabled(el) });
    }
    return out;
}

function pageRef(cat: HelpCatalogue, missing: string[]): SectionRef | null {
    const el = document.querySelector<HTMLElement>(sel(ATTR.page));
    return sectionRef(el, cat, missing, ATTR.page);
}

/** Nearest visible section to a box (0 distance when they overlap). */
function nearestSection(box: Box, cat: HelpCatalogue, missing: string[]): SectionRef | null {
    const c = centre(box);
    let best: HTMLElement | null = null;
    let bestD = Infinity;
    for (const el of tagged(ATTR.section)) {
        const key = el.getAttribute(ATTR.section) ?? '';
        if (!cat.sections[key]) continue;
        const r = rectOf(el);
        const cc = centre(r);
        const d = intersects(r, box) ? 0 : Math.hypot(cc.x - c.x, cc.y - c.y);
        if (d < bestD || (d === bestD && best && areaOf(r) < areaOf(rectOf(best)))) { best = el; bestD = d; }
    }
    return best ? sectionRef(best, cat, missing) : null;
}

/** The section a box "falls in": smallest containing its centre, else biggest overlap. */
function sectionForBox(box: Box, cat: HelpCatalogue, missing: string[]): SectionRef | null {
    const c = centre(box);
    let best: HTMLElement | null = null;
    let bestArea = Infinity;
    let overlap: HTMLElement | null = null;
    let overlapArea = 0;
    for (const el of tagged(ATTR.section)) {
        const key = el.getAttribute(ATTR.section) ?? '';
        const r = rectOf(el);
        if (!intersects(r, box)) continue; // only sections the box touches are worth logging or explaining
        if (!cat.sections[key]) { missing.push(key); continue; }
        if (hasPoint(r, c.x, c.y) && areaOf(r) < bestArea) { best = el; bestArea = areaOf(r); }
        if (intersects(r, box)) {
            const ia = areaOf(intersection(r, box));
            if (ia > overlapArea) { overlap = el; overlapArea = ia; }
        }
    }
    return sectionRef(best ?? overlap, cat, missing);
}

/** Nearest section ancestor that HAS catalogue text; ancestors whose key is missing are logged and skipped. */
function sectionAncestor(el: Element | null, cat: HelpCatalogue, missing: string[]): SectionRef | null {
    let cur = el?.closest<HTMLElement>(sel(ATTR.section)) ?? null;
    while (cur) {
        const ref = sectionRef(cur, cat, missing);
        if (ref) return ref;
        cur = cur.parentElement?.closest<HTMLElement>(sel(ATTR.section)) ?? null;
    }
    return null;
}

const dedupe = (xs: string[]) => Array.from(new Set(xs.filter(Boolean)));

/** Section chain (section -> page -> missing) shared by every entry point. */
function fromSection(section: SectionRef | null, via: 'section' | 'fallback-section', cat: HelpCatalogue, missing: string[]): Resolution {
    if (section) {
        const feats = featureRefs(tagged(ATTR.element, section.el), cat, missing);
        return { target: { kind: 'area', via, section, features: feats }, missingKeys: dedupe(missing) };
    }
    const page = pageRef(cat, missing);
    if (page) {
        const feats = featureRefs(tagged(ATTR.element, page.el), cat, missing);
        return { target: { kind: 'area', via: 'page', section: page, features: feats }, missingKeys: dedupe(missing) };
    }
    return { target: { kind: 'missing', key: dedupe(missing)[0] ?? null }, missingKeys: dedupe(missing) };
}

function elementTarget(el: HTMLElement, cat: HelpCatalogue, missing: string[]): Resolution {
    const key = el.getAttribute(ATTR.element) ?? '';
    const entry = cat.elements[key];
    if (!entry) {
        if (key) missing.push(key);
        return fromSection(sectionAncestor(el, cat, missing), 'fallback-section', cat, missing);
    }
    return {
        target: { kind: 'element', key, el, entry, disabled: readDisabled(el), section: sectionAncestor(el, cat, []) },
        missingKeys: dedupe(missing),
    };
}

const TABLE_CELL = 'th, td, [role="columnheader"], [role="cell"], [role="gridcell"]';

/** Elements a mouse click explains on their own: marked solo, or inside a table cell (column headers, row actions). */
const isSolo = (el: HTMLElement) => el.hasAttribute(ATTR.solo) || el.closest(TABLE_CELL) !== null;

/** Drop / tap: explain the one thing at the point; fall back to its section, then the page. */
export function resolveAtPoint(x: number, y: number, cat: HelpCatalogue): Resolution {
    const missing: string[] = [];
    const el = taggedAtPoint(ATTR.element, x, y);
    if (el) return elementTarget(el, cat, missing);
    return fromSection(sectionRef(taggedAtPoint(ATTR.section, x, y), cat, missing), 'fallback-section', cat, missing);
}

/** Keyboard: Enter on a focused tagged element explains that element (same chain as a drop). */
export function resolveElement(el: HTMLElement, cat: HelpCatalogue): Resolution {
    return elementTarget(el, cat, []);
}

/** Click a section: the whole section at the point is explained. */
export function resolveSectionAtPoint(x: number, y: number, cat: HelpCatalogue): Resolution {
    const missing: string[] = [];
    const el = taggedAtPoint(ATTR.element, x, y);
    // A tile, an action item, a column header or a row action stands on its own: explain it, its card offers "Explain this section".
    if (el && isSolo(el) && cat.elements[el.getAttribute(ATTR.element) ?? '']) return elementTarget(el, cat, missing);
    // A click lands on an element inside the section; explain the section it belongs to.
    const sec = el ? sectionAncestor(el, cat, missing) : sectionAncestor(taggedAtPoint(ATTR.section, x, y), cat, missing);
    return fromSection(sec, 'section', cat, missing);
}

/** The "Explain this section" button on an element card. */
export function resolveSectionOf(section: SectionRef, cat: HelpCatalogue): Resolution {
    return fromSection(section, 'section', cat, []);
}

/** Box select: every tagged element the box touches, in screen order, under the section it falls in. */
export function resolveBox(box: Box, cat: HelpCatalogue): Resolution {
    const missing: string[] = [];
    const els = tagged(ATTR.element).filter(el => intersects(rectOf(el), box));
    const feats = featureRefs(els, cat, missing);
    if (feats.length) {
        const section = sectionForBox(box, cat, missing);
        return { target: { kind: 'area', via: 'box', section, features: feats, box }, missingKeys: dedupe(missing) };
    }
    // Nothing tagged in the box: the nearest tagged section explains itself, else the page.
    const near = sectionForBox(box, cat, missing) ?? nearestSection(box, cat, missing);
    return fromSection(near, 'fallback-section', cat, missing);
}
